import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Alert} from "@interstate/components/Alert";
import {Badge} from "@interstate/components/Badge";
import {Box} from "@interstate/components/Box";
import {Button} from "@interstate/components/Button";
import {Spinner} from "@interstate/components/Spinner";
import {TextInput} from "@interstate/components/TextInput";
import {Typography} from "@interstate/components/Typography";
import {Table, TableColumn} from "@interstate/components/Table";
import {OnRowFunction} from "@interstate/components/Table/Types/tableTypes";
import {BuildingOfficeIcon} from "@interstate/components/Icons";
import {useNavigation} from "../../../navigation";
import {EntityStatusBar} from "../../badges";
import {OperationalTableData} from "../search/table-data";
import {useTenantOrgs} from "../hooks/useTenantOrgs";
import "./TenantSiblingsTable.scss";

function formatAddress(record: OperationalTableData): string {
  return [
    record.streetAddressLine1,
    record.streetAddressLine2,
    record.city,
    record.state
  ]
    .filter(part => part)
    .join(", ");
}

export interface TenantSiblingsTableProps {
  currentEntityId: string;
  tenantId: string | undefined;
}

export const TenantSiblingsTable: FC<TenantSiblingsTableProps> = ({
  currentEntityId,
  tenantId
}) => {
  const {t} = useTranslation();
  const {navigateToSearch, navigateToOperationalEntity} = useNavigation();
  const location = useLocation();
  const {orgs, loading, error, truncated, fetch} = useTenantOrgs(tenantId);

  const onRowCallback = useCallback<OnRowFunction<OperationalTableData>>(
    record => ({
      onClick: (event: React.MouseEvent) =>
        navigateToOperationalEntity(record.key, event, location.pathname)
    }),
    [navigateToOperationalEntity, location.pathname]
  );
  const [filter, setFilter] = useState("");

  useEffect(() => {
    void fetch();
  }, [fetch]);

  const columns = useMemo<TableColumn[]>(
    () => [
      {
        title: t("tenancy.siblings.columns.name"),
        key: "name",
        className: "tenant-sibling-name",
        columnSortable: true,
        sorter: (a: OperationalTableData, b: OperationalTableData) =>
          a.name.localeCompare(b.name),
        render: (_text: string, record: OperationalTableData) => (
          <div className={"tenant-sibling-name-cell"}>
            <span className={"tenant-sibling-dba-name"}>{record.name}</span>
            {record.key === currentEntityId && (
              <Badge
                id={`tenant-sibling-current-${record.key}`}
                data-testid={`tenant-sibling-current-${record.key}`}
                variant={"info"}>
                {t("tenancy.siblings.current")}
              </Badge>
            )}
          </div>
        )
      },
      {
        title: t("tenancy.siblings.columns.address"),
        key: "address",
        className: "tenant-sibling-address",
        columnSortable: true,
        sorter: (a: OperationalTableData, b: OperationalTableData) =>
          formatAddress(a).localeCompare(formatAddress(b)),
        render: (_text: string, record: OperationalTableData) =>
          formatAddress(record)
      },
      {
        title: t("tenancy.siblings.columns.status"),
        key: "status",
        className: "tenant-sibling-status",
        columnSortable: true,
        sorter: (a: OperationalTableData, b: OperationalTableData) =>
          a.statuses.join(", ").localeCompare(b.statuses.join(", ")),
        render: (_text: string, record: OperationalTableData) => (
          <EntityStatusBar
            id={record.key}
            statuses={record.statuses}
            compact={false}
          />
        )
      }
    ],
    [currentEntityId, t]
  );

  const filteredOrgs = useMemo<OperationalTableData[]>(() => {
    const term = filter.trim().toLowerCase();
    if (!term) {
      return orgs;
    }
    return orgs.filter(
      org =>
        org.name.toLowerCase().includes(term) ||
        formatAddress(org).toLowerCase().includes(term)
    );
  }, [orgs, filter]);

  if (loading) {
    return (
      <Box
        className={"tenant-siblings-loading"}
        data-testid={"tenant-siblings-loading"}>
        <Spinner size={"small"} />
        <Typography tag={"span"} variant={"body-sm"}>
          {t("tenancy.siblings.loading")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        id={"tenant-siblings-error"}
        data-testid={"tenant-siblings-error"}
        type={"error"}>
        {t("tenancy.siblings.error")}
      </Alert>
    );
  }

  if (orgs.length === 0) {
    return (
      <Alert
        id={"tenant-siblings-empty"}
        data-testid={"tenant-siblings-empty"}
        type={"info"}>
        {t("tenancy.siblings.empty")}
      </Alert>
    );
  }

  return (
    <Box
      className={"tenant-siblings"}
      data-testid={"tenant-siblings"}>
      <Box className={"tenant-siblings-count"}>
        <BuildingOfficeIcon className={"tenant-siblings-count-icon"} />
        <Typography tag={"span"} variant={"label-sm"}>
          {t("tenancy.siblings.count-label")}
        </Typography>
        <Badge
          id={"tenant-siblings-count-badge"}
          data-testid={"tenant-siblings-count"}
          variant={"info"}>
          {orgs.length}
        </Badge>
      </Box>
      {truncated && (
        <Alert
          id={"tenant-siblings-truncation"}
          data-testid={"tenant-siblings-truncation"}
          type={"caution"}>
          {t("tenancy.siblings.truncated.message")}
          <Button
            id={"tenant-siblings-truncation-link"}
            data-testid={"tenant-siblings-truncation-link"}
            buttonStyle={"tertiary"}
            onClick={event => navigateToSearch(event, `tenantid:${tenantId}`)}>
            {t("tenancy.siblings.truncated.link")}
          </Button>
        </Alert>
      )}
      <TextInput
        id={"tenant-siblings-filter"}
        data-testid={"tenant-siblings-filter"}
        name={"tenant-siblings-filter"}
        label={t("tenancy.siblings.filter-label") || "Filter"}
        displayLabel={false}
        placeholder={t("tenancy.siblings.filter-placeholder")}
        value={filter}
        onChange={event => setFilter(event.target?.value ?? "")}
      />
      <Table
        id={"tenant-siblings-table"}
        data-testid={"tenant-siblings-table"}
        columns={columns}
        data={filteredOrgs}
        onRowCallback={onRowCallback}
        sortableColumns={true}
        defaultPageSize={100}
        rowClassName={(record: OperationalTableData) =>
          record.key === currentEntityId ? "tenant-sibling-current-row" : ""
        }
        bordered={true}
        borderType={"contained"}
        size={"small"}
        dataDensity={"small"}
        highlightOnHover={true}
      />
    </Box>
  );
};
